import { findFirstNonEmptyString, PageType } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { tracking } from "shared/data";

import type { articleMoreStoriesItemArticle$key } from "scmp-app/queries/__generated__/articleMoreStoriesItemArticle.graphql";

import {
  Section,
  StyledArticleMoreStoriesItem,
  StyledBaseImage,
  StyledEntityLink,
  Title,
} from "./styles";

export type Props = {
  className?: string;
  reference: articleMoreStoriesItemArticle$key;
};

export const ArticleMoreStoriesItem: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const postiesArticleMoreStoriesQueryParameters = {
    module: tracking.module.MoreStoriesPosties,
    pgtype: tracking.pageType.ArticlePosties,
  };

  const article = useFragment(
    graphql`
      fragment articleMoreStoriesItemArticle on Article {
        ...entityLink
        headline
        socialHeadline
        sections {
          value {
            name
            urlAlias
          }
        }
        images {
          url
          size768x768: style(filter: { style: "768x768" }) {
            url
          }
        }
      }
    `,
    reference_,
  );

  const renderImage = (article: Article) => {
    const mainImage = first(article.images);
    const source = mainImage?.size768x768?.url ?? mainImage?.url;
    return (
      source && (
        <StyledEntityLink
          query={{ pageType: PageType.PostiesArticle, ...postiesArticleMoreStoriesQueryParameters }}
          reference={article}
        >
          <StyledBaseImage
            $aspectRatio="1"
            $objectFit="cover"
            alt={article.headline ?? undefined}
            disableLinkingData
            src={source}
          />
        </StyledEntityLink>
      )
    );
  };

  const renderSection = (article: Article) => {
    const sections = article.sections;
    const firstSection = first(sections);

    if (!firstSection?.value) {
      return null;
    }

    const section = last(firstSection?.value);

    return (
      section && (
        <Section
          pathname={section.urlAlias}
          query={{
            pageType: PageType.PostiesSection,
            ...postiesArticleMoreStoriesQueryParameters,
          }}
        >
          {section.name}
        </Section>
      )
    );
  };

  const renderTitle = (article: Article) => (
    <Title
      query={{ pageType: PageType.PostiesArticle, ...postiesArticleMoreStoriesQueryParameters }}
      reference={article}
    >
      {findFirstNonEmptyString(article.socialHeadline, article.headline)}
    </Title>
  );

  return (
    <StyledArticleMoreStoriesItem>
      {renderImage(article)}
      {renderSection(article)}
      {renderTitle(article)}
    </StyledArticleMoreStoriesItem>
  );
  type Article = Exclude<typeof article, undefined>;
};

ArticleMoreStoriesItem.displayName = "ArticleMoreStoriesItem";
